// Internal identity seam for checkout. The checkout flows and the identity gate
// go through one IdentityAdapter so they never branch on auth_mode: BYO yields
// the publisher adapter's plaintext { email } and logs in by navigating away;
// ezAuth yields an { ezAuthToken } from the in-page ezAuth SDK (the server
// resolves it to an email so the plaintext never reaches the page) and logs in /
// registers with ezAuth's mounted in-page form. ezAuth is the default when
// auth_mode is absent.

import type { AuthAdapter, AuthMode, Credential } from '@/types';
import { getAdapter } from '@/authAdapter';
import { ensureEzAuth, ensureEzAuthFacade, signOutEzAuth, type EzAuthFormScreen } from '@/ensureEzAuth';
import { logError } from '@/sdkConsole';

// Capabilities the identity gate reads to render mode-appropriate choices without
// inspecting auth_mode itself.
export interface IdentityCapabilities {
  // Login/register happen in-page (ezAuth's mounted form) and resolve the same
  // checkout. When false (BYO) login navigates away via the publisher's flow and
  // can't be awaited in-page.
  loginInPage: boolean;
  // Whether the gate offers a "Create account" action. Both modes support it:
  // ezAuth mounts its in-page register form; BYO navigates away to the
  // publisher's registration flow.
  canCreateAccount: boolean;
}

// An in-progress interactive login/register started from the gate.
export interface IdentityPrompt {
  // Resolves with the visitor's credential once they authenticate in-page, or
  // null if the prompt is closed first (or for navigate-away logins, which never
  // resolve because the page unloads).
  completed: Promise<Credential | null>;
  // Tear down any mounted form / listeners. Idempotent.
  close: () => void;
}

// Optional knobs for an in-page ezAuth prompt. BYO ignores these (navigate-away).
export interface IdentityPromptOptions {
  // Forwarded to ezAuth.mount so hosts can sync external chrome when the form
  // switches screens in place (account_exists sign-in link → login, forgot, etc.).
  onScreenChange?: (screen: EzAuthFormScreen) => void;
}

export interface IdentityAdapter {
  // Resolves the logged-in visitor's checkout credential, or null when the
  // visitor is anonymous (the gate then offers login/register/guest). Never throws.
  resolveCredential: () => Promise<Credential | null>;
  capabilities: IdentityCapabilities;
  // Begin an interactive login. ezAuth mounts its in-page form into `target` and
  // resolves `completed` once authenticated; BYO triggers the publisher's
  // goToLogin() (target unused) and navigates away.
  promptLogin: (target?: HTMLElement, options?: IdentityPromptOptions) => IdentityPrompt;
  // Begin account creation. ezAuth mounts its in-page register form into `target`
  // and resolves `completed` once authenticated; BYO triggers the publisher's
  // goToCreateAccount() (target unused) and navigates away.
  promptRegister: (target?: HTMLElement, options?: IdentityPromptOptions) => IdentityPrompt;
  // Ends the visitor's site-level identity, or null when the mode can't. ezAuth
  // revokes via signOutEzAuth; BYO is the publisher adapter's logout, or null
  // when none was registered — the "Not you?" switch keys off non-null.
  logout: (() => Promise<void>) | null;
}

// getIdentityAdapter returns the identity seam for the domain's auth mode. BYO
// uses the publisher-registered adapter; every other mode (ezauth, the default)
// uses the internal ezAuth SDK.
export function getIdentityAdapter(authMode: AuthMode | undefined, domainId: number | undefined): IdentityAdapter {
  if (authMode === 'byo') {
    return byoAdapter();
  }
  return ezAuthAdapter(domainId);
}

function byoAdapter(): IdentityAdapter {
  return {
    async resolveCredential() {
      const adapter = getAdapter();
      if (!adapter) {
        return null;
      }
      try {
        // Token first: a registered getIdentityToken yields the SM-minted
        // identity token, so the plaintext email never enters page JS.
        if (typeof adapter.getIdentityToken === 'function') {
          const token = await adapter.getIdentityToken();
          const trimmedToken = typeof token === 'string' ? token.trim() : '';
          if (trimmedToken) {
            return { identityToken: trimmedToken };
          }
        }
        // Fall back to the plaintext email when the adapter provides it (a
        // token-only adapter may not, in which case there is no credential).
        if (typeof adapter.getUserEmail === 'function') {
          const email = await adapter.getUserEmail();
          const trimmed = typeof email === 'string' ? email.trim() : '';
          return trimmed ? { email: trimmed } : null;
        }
        return null;
      } catch (err) {
        logError('auth adapter identity resolution failed', err);
        return null;
      }
    },
    capabilities: { loginInPage: false, canCreateAccount: true },
    promptLogin: () => byoNavigateAwayPrompt((adapter) => adapter.goToLogin(), 'goToLogin'),
    promptRegister: () => byoNavigateAwayPrompt((adapter) => adapter.goToCreateAccount(), 'goToCreateAccount'),
    logout: byoLogout(),
  };
}

// byoLogout resolves the publisher adapter's logout at adapter-build time.
// getIdentityAdapter rebuilds the adapter on every call, so a late initialize
// is picked up on the next render. null when the publisher registered no
// logout — the "Not you?" switch is hidden then. Failures propagate so
// checkout cannot continue as a different identity after a failed sign-out.
function byoLogout(): (() => Promise<void>) | null {
  const adapter = getAdapter();
  if (!adapter?.logout) {
    return null;
  }
  return async () => {
    await adapter.logout!();
  };
}

function ezAuthAdapter(domainId: number | undefined): IdentityAdapter {
  return {
    async resolveCredential() {
      if (typeof domainId !== 'number') {
        return null;
      }
      // ensureEzAuth reads the logged-in visitor's access token (or null when
      // anonymous / unavailable) and never throws.
      const token = await ensureEzAuth(domainId);
      return token ? { ezAuthToken: token } : null;
    },
    capabilities: { loginInPage: true, canCreateAccount: true },
    promptLogin: (target, options) => ezAuthPrompt(domainId, target, 'login', options),
    promptRegister: (target, options) => ezAuthPrompt(domainId, target, 'register', options),
    logout: typeof domainId === 'number' ? () => signOutEzAuth(domainId) : null,
  };
}

// byoNavigateAwayPrompt triggers a publisher navigate-away (goToLogin /
// goToCreateAccount) and returns a prompt that only settles if closed: BYO auth
// navigates away (or opens the publisher's own modal), so there is nothing to
// await in-page — the visitor returns authenticated via the publisher flow and
// authChanged, not this prompt.
function byoNavigateAwayPrompt(navigate: (adapter: AuthAdapter) => void | Promise<void>, action: 'goToLogin' | 'goToCreateAccount'): IdentityPrompt {
  const adapter = getAdapter();
  if (adapter) {
    // navigate may be sync or async; catch a synchronous throw and an async
    // rejection so neither is silently swallowed.
    try {
      Promise.resolve(navigate(adapter)).catch((err) => {
        logError(`auth adapter ${action} failed`, err);
      });
    } catch (err) {
      logError(`auth adapter ${action} failed`, err);
    }
  }
  let settle: (credential: Credential | null) => void = () => {};
  const completed = new Promise<Credential | null>((resolve) => {
    settle = resolve;
  });
  return { completed, close: () => settle(null) };
}

// ezAuthPrompt mounts ezAuth's in-page login/register form into `target` and
// resolves with the visitor's token credential once they authenticate. Closing
// (Back, or the step unmounting) tears the form down and resolves null.
function ezAuthPrompt(
  domainId: number | undefined,
  target: HTMLElement | undefined,
  mode: 'login' | 'register',
  options?: IdentityPromptOptions,
): IdentityPrompt {
  let settle: (credential: Credential | null) => void = () => {};
  const completed = new Promise<Credential | null>((resolve) => {
    settle = resolve;
  });

  let done = false;
  let unmount: (() => void) | null = null;
  let unsubscribe: (() => void) | null = null;

  const finish = (credential: Credential | null) => {
    if (done) {
      return;
    }
    done = true;
    if (unsubscribe) {
      unsubscribe();
    }
    if (unmount) {
      unmount();
    }
    settle(credential);
  };

  void (async () => {
    if (typeof domainId !== 'number' || !target) {
      finish(null);
      return;
    }
    const facade = await ensureEzAuthFacade(domainId);
    if (done) {
      return;
    }
    if (!facade) {
      finish(null);
      return;
    }
    // Subscribe before mounting. onChange fires immediately with the current
    // (anonymous) state, which is ignored; a later 'authenticated' transition
    // means the visitor logged in via the form, so read the token and finish.
    unsubscribe = facade.onChange((state) => {
      if (state.status !== 'authenticated') {
        return;
      }
      void ensureEzAuth(domainId).then((token) => {
        finish(token ? { ezAuthToken: token } : null);
      });
    });
    if (done) {
      return;
    }
    try {
      unmount = facade.mount(target, {
        mode,
        showToggle: false,
        // EmailStep renders its own social buttons beside the form.
        socialProviders: false,
        onScreenChange: options?.onScreenChange,
      }).unmount;
    } catch (err) {
      logError('ezAuth.mount failed; ezAuth identity is disabled here', err);
      finish(null);
    }
  })();

  return { completed, close: () => finish(null) };
}
